import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PostEditor } from '../components/post/PostEditor';
import { postsApi } from '../api/posts';
import type { UpdatePostRequest, CreatePostRequest } from '../types/post';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../store';
import { updatePost } from '../store/slices/postSlice';
import { usePosts } from '../hooks/usePosts';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { useLanguage } from '../i18n/useLanguage';

export const EditPostPage: React.FC = () => {
  const { t } = useLanguage();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { currentPost, fetchPost, loading, error: fetchError } = usePosts();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchPost(id).catch((err) => {
        console.error('Failed to fetch post:', err);
      });
    }
  }, [id, fetchPost]);

  const handleSubmit = async (data: UpdatePostRequest | CreatePostRequest) => {
    if (!id) return;

    setIsSubmitting(true);
    setError(null);
    
    // Ensure content is provided for update
    if (!data.content) {
      setError('Content is required');
      setIsSubmitting(false);
      return;
    }

    try {
      const updateData: UpdatePostRequest = {
        title: data.title,
        content: data.content,
        reward: '',
        sightingType: 'sightingType' in data ? data.sightingType : undefined,
        tags: data.tags,
      };
      if ('language' in data && data.language && data.language.toLowerCase() !== 'en') {
        updateData.language = data.language;
      }
      const updatedPost = await postsApi.updatePost(id, updateData);
      dispatch(updatePost(updatedPost));
      navigate(`/posts/${id}`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update post';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (id) {
      navigate(`/posts/${id}`);
    } else {
      navigate('/');
    }
  };

  if (loading) {
    return <LoadingSpinner size="large" />;
  }

  if (fetchError) {
    return (
      <div className="edit-post-page">
        <div className="error-message">Error loading post: {fetchError}</div>
        <button onClick={() => id && fetchPost(id)}>Retry</button>
      </div>
    );
  }

  if (!currentPost) {
    return <div>Post not found</div>;
  }

  return (
    <div className="edit-post-page">
      <h1>{t('post.editTitle')}</h1>
      {error && <div className="error-message">{error}</div>}
      <PostEditor
        post={currentPost}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isLoading={isSubmitting}
      />
    </div>
  );
};

